import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
	DroneCard,
	DroneInfo,
} from '../../components/features/provider/DroneCard';
import {
	AvailableDroneSn,
	DroneCreateEditPanel,
	DronePerfForm,
} from '../../components/features/provider/DroneCreateEditPanel';
import { DroneDetailsPanel } from '../../components/features/provider/DroneDetailsPanel';
import {
	FleetSection,
	FleetTabs,
} from '../../components/features/provider/FleetTabs';
import { Body } from '../../components/layout/Body';
import { IconSearch } from '../../components/Icons';
import { Title } from '../../components/layout/Title';
import { Page } from '../../components/ui/Page';
import { useDialog } from '../../hooks/useDialog';
import Store from '../../store/Store';

interface FleetManagementPageProps {
	section: FleetSection;
}

const HEIGHT_OFFSET = 399;
const MIN_LAYOUT_HEIGHT = 260;

const emptyPerf: DronePerfForm = {
	reachDiameter: '3000',
	flightSpeed: '900',
	startStopTime: '1.5',
	maxFlightTime: '27',
};

export default function FleetManagementPage({ section }: FleetManagementPageProps) {
	const { showConfirmDialog } = useDialog();
	const mode = Store.use('mode');
	const drones = Store.use('drones') as DroneInfo[] | undefined;
	const availableDroneSns = Store.use('availableDroneSns') as
		| AvailableDroneSn[]
		| undefined;

	const [selectedDroneId, setSelectedDroneId] = useState<number | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [isEditOpen, setIsEditOpen] = useState(false);
	const [name, setName] = useState('');
	const [selectedSn, setSelectedSn] = useState('');
	const [isSnDropdownOpen, setIsSnDropdownOpen] = useState(false);
	const [perf, setPerf] = useState(emptyPerf);
	const [saving, setSaving] = useState(false);
	const [layoutHeight, setLayoutHeight] = useState(MIN_LAYOUT_HEIGHT);

	const visibleAvailableDroneSns = availableDroneSns ?? [];
	const isFormOpen = isCreateOpen || isEditOpen;
	const isRightPanelOpen = isFormOpen || selectedDroneId !== null;

	useEffect(() => {
		const updateLayoutHeight = () => {
			const hasPageScroll =
				document.documentElement.scrollHeight >
				document.documentElement.clientHeight;

			const pageHeight = hasPageScroll
				? document.documentElement.scrollHeight
				: window.innerHeight;

			setLayoutHeight(
				Math.max(pageHeight - HEIGHT_OFFSET, MIN_LAYOUT_HEIGHT)
			);
		};

		updateLayoutHeight();

		const observer = new ResizeObserver(updateLayoutHeight);

		observer.observe(document.body);
		observer.observe(document.documentElement);

		window.addEventListener('resize', updateLayoutHeight);

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', updateLayoutHeight);
		};
	}, []);
	useEffect(() => {
		const updateLayoutHeight = () => {
			const hasPageScroll =
				document.documentElement.scrollHeight >
				document.documentElement.clientHeight;

			const pageHeight = hasPageScroll
				? document.documentElement.scrollHeight
				: window.innerHeight;

			setLayoutHeight(
				Math.max(pageHeight - HEIGHT_OFFSET, MIN_LAYOUT_HEIGHT)
			);
		};

		updateLayoutHeight();

		const frame = requestAnimationFrame(updateLayoutHeight);
		const timeout = setTimeout(updateLayoutHeight, 50);

		return () => {
			cancelAnimationFrame(frame);
			clearTimeout(timeout);
		};
	}, [isRightPanelOpen]);

	useEffect(() => {
		if (section === 'drones') {
			Store.loadDrones();
			Store.loadAvailableDroneSns();
		}
	}, [section]);

	useEffect(() => {
		setSelectedDroneId(null);
		setIsCreateOpen(false);
		setIsEditOpen(false);
	}, [section]);

	const selectedSnInfo = visibleAvailableDroneSns.find(
		(item) => item.sn === selectedSn
	);
	const visibleDrones = drones ?? [];
	const selectedDrone = visibleDrones.find((drone) => drone.id === selectedDroneId);

	const selectedDroneStatus: 'ONLINE' | 'OFFLINE' | undefined =
		selectedDrone?.deviceOnlineStatus === undefined
			? undefined
			: selectedDrone.deviceOnlineStatus
				? 'ONLINE'
				: 'OFFLINE';

	const selectedSnLabel = selectedSnInfo
		? `${selectedSnInfo.callsign} (${selectedSnInfo.sn})${selectedSnInfo.online ? ' ✓' : ''}`
		: 'SN auswählen...';

	const openCreate = () => {
		setSelectedDroneId(null);
		setIsCreateOpen(true);
		setIsEditOpen(false);
		setName('');
		setSelectedSn('');
		setIsSnDropdownOpen(false);
		setPerf(emptyPerf);
	};

	const cancelCreate = () => {
		setIsCreateOpen(false);
		setIsEditOpen(false);
		setName('');
		setSelectedSn('');
		setIsSnDropdownOpen(false);
		setPerf(emptyPerf);
	};

	const openEdit = (drone: DroneInfo) => {
		setSelectedDroneId(drone.id);
		setIsCreateOpen(false);
		setIsEditOpen(true);
		setName(drone.name);
		setSelectedSn(drone.serialNumber ?? '');
		setIsSnDropdownOpen(false);
		setPerf({
			reachDiameter: String(drone.droneType.reachDiameter),
			flightSpeed: String(drone.droneType.flightSpeed),
			startStopTime: String(drone.droneType.startStopTime),
			maxFlightTime: String(drone.droneType.maxFlightTime),
		});
	};

	const cancelForm = () => {
		setIsCreateOpen(false);
		setIsEditOpen(false);
		setIsSnDropdownOpen(false);

		if (isCreateOpen) {
			setName('');
			setSelectedSn('');
			setPerf(emptyPerf);
		}
	};

	const handleDeleteDrone = (drone: DroneInfo) => {
		showConfirmDialog({
			title: 'Drohne löschen',
			text: `Sind Sie sicher, dass Sie "${drone.name}" löschen möchten?`,
			confirmLabel: 'Ja',
			cancelLabel: 'Nein',
			onConfirm: () => {
				Store.deleteDrone(drone.id);
				setSelectedDroneId(null);
			},
		});
	};

	const toggleSelectedDrone = (droneId: number) => {
		setIsCreateOpen(false);
		setIsEditOpen(false);
		setIsSnDropdownOpen(false);
		setSelectedDroneId((currentDroneId) =>
			currentDroneId === droneId ? null : droneId
		);
	};

	const perfValid =
		perf.reachDiameter !== '' &&
		perf.flightSpeed !== '' &&
		perf.startStopTime !== '' &&
		perf.maxFlightTime !== '';

	const canSave =
		name.trim().length > 0 &&
		perfValid &&
		(isEditOpen || selectedSn.length > 0);

	const handleSaveDrone = async () => {
		setSaving(true);

		try {
			if (isCreateOpen) {
				await Store.createDrone(
					name.trim(),
					selectedSn,
					Number(perf.reachDiameter),
					Number(perf.flightSpeed),
					Number(perf.startStopTime),
					Number(perf.maxFlightTime)
				);

				Store.setPopupMessage(
					'ok',
					'Drohne erstellt',
					`"${name}" wurde erfolgreich erstellt.`
				);

				cancelCreate();
			} else if (isEditOpen && selectedDrone) {
				await Store.updateDrone(
					selectedDrone.id,
					name.trim(),
					Number(perf.reachDiameter),
					Number(perf.flightSpeed),
					Number(perf.startStopTime),
					Number(perf.maxFlightTime)
				);

				Store.setPopupMessage(
					'ok',
					'Drohne aktualisiert',
					`"${name}" wurde erfolgreich gespeichert.`
				);

				setIsEditOpen(false);
			}
		} catch {
			// errors shown by Store
		} finally {
			setSaving(false);
		}
	};

	const filteredDrones = visibleDrones.filter((drone) => {
		const value = searchTerm.trim().toLowerCase();

		if (!value) {
			return true;
		}

		return [
			drone.name,
			drone.callsign,
			drone.serialNumber,
			drone.droneType.name,
		]
			.filter(Boolean)
			.some((field) => field?.toLowerCase().includes(value));
	});


	if (mode !== 'PROVIDER') {
		return <Navigate to="/dashboard" replace />;
	}

	return (
		<Page>
			<Body>
				<div className="flex mb-6 items-center gap-8">
					<Title>Flottenmanagement</Title>

					<FleetTabs section={section} />
				</div>

				<div
					className="h-px bg-[#B8D2EB]"
					style={{
						width: '100vw',
						marginLeft: 'calc(50% - 50vw)',
					}}
				/>

				{section === 'drones' && (
					<div
						className="mt-8 flex w-full items-stretch gap-12 overflow-visible"
						style={{
							height: isRightPanelOpen ? undefined : layoutHeight,
							minHeight: isRightPanelOpen ? layoutHeight : undefined,
						}}
					>
						<div
							className="flex w-[48%] max-w-[720px] flex-col p-4 pb-0"
							style={{ height: layoutHeight }}
						>
							<div className="mb-6 flex items-center gap-6">
								<label className="fleet-search-field flex h-12 flex-1 items-center gap-3 rounded-full border border-[#333333] bg-white px-5">
									<span className="sr-only">Drohnen suchen</span>
									<IconSearch />
									<input
										type="search"
										aria-label="Drohnen suchen"
										value={searchTerm}
										onChange={(event) => setSearchTerm(event.target.value)}
										placeholder="Suche"
										className="fleet-search-input min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-[#8D8C8C]"
									/>
								</label>

								<button
									type="button"
									onClick={openCreate}
									className="h-12 shrink-0 rounded-full bg-[#E1320F] px-8 text-sm font-semibold text-white transition hover:bg-[#c62c0d]"
								>
									Drohne hinzufügen
								</button>
							</div>

							<div
								className="fleet-drone-list-scroll min-h-0 flex-1 overflow-y-auto pr-5"
								tabIndex={0}
								aria-label="Drohnenliste"
							>
								{filteredDrones.length > 0 ? (
									<div className="flex flex-col gap-4 pb-6">
										{filteredDrones.map((drone) => (
											<DroneCard
												key={drone.id}
												drone={drone}
												selected={selectedDroneId === drone.id}
												onClick={() => toggleSelectedDrone(drone.id)}
											/>
										))}
									</div>
								) : (
									<div className="text-sm text-gray-500">
										Keine Drohnen vorhanden.
									</div>
								)}
							</div>
						</div>

						<div
							className={
								'flex flex-1 justify-center pr-1 pb-6 ' +
								(isRightPanelOpen ? 'items-start' : 'items-center')
							}
						>
							{isFormOpen ? (
								<DroneCreateEditPanel
									mode={isCreateOpen ? 'create' : 'edit'}
									name={name}
									perf={perf}
									availableDroneSns={visibleAvailableDroneSns}
									selectedSn={selectedSn}
									selectedSnInfo={selectedSnInfo}
									selectedSnLabel={selectedSnLabel}
									isSnDropdownOpen={isSnDropdownOpen}
									canSave={canSave}
									saving={saving}
									onNameChange={setName}
									onPerfChange={setPerf}
									onSelectedSnChange={setSelectedSn}
									onSnDropdownOpenChange={setIsSnDropdownOpen}
									onCancel={cancelForm}
									onSave={handleSaveDrone}
								/>
							) : selectedDrone ? (
								<DroneDetailsPanel
									drone={selectedDrone}
									status={selectedDroneStatus}
									onDelete={handleDeleteDrone}
									onEdit={openEdit}
								/>
							) : selectedDroneId === null ? (
								<img
									src="/assets/dashboard-bg.svg"
									alt=""
									className="h-auto max-h-[240px] w-auto max-w-[320px]"
								/>
							) : null}
						</div>
					</div>
				)}
			</Body>
		</Page>
	);
}